import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterableDataTable, DrawerPanel, ConfirmDialog } from '../../components/shared';
import type { RowAction, DrawerAction } from '../../components/shared';
import { MachineTypeControllerApi, ToolControllerApi, SafetyWarningControllerApi } from '../../api-generated/apis';
import type { MachineType } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

interface MachineTypeRow {
  id: string | number;
  name: string;
  description: string;
}

const machineTypeApi = new MachineTypeControllerApi(apiConfig);
const toolApi = new ToolControllerApi(apiConfig);
const warningApi = new SafetyWarningControllerApi(apiConfig);

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'description', headerName: 'Description', flex: 2, minWidth: 200 },
];

const MachineTypesPanel: React.FC = () => {
  const queryClient = useQueryClient();

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formToolIds, setFormToolIds] = useState<string[]>([]);
  const [formWarningIds, setFormWarningIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Queries
  const { data: machineTypes, isLoading } = useQuery<MachineType[]>({
    queryKey: ['machine-types'],
    queryFn: () => machineTypeApi.getAllMachineTypes(),
  });

  const toolsQuery = useQuery<any[]>({
    queryKey: ['tools'],
    queryFn: () => toolApi.getAll() as any,
  });

  const warningsQuery = useQuery<any[]>({
    queryKey: ['safety-warnings'],
    queryFn: () => warningApi.getAll1() as any,
  });

  // Transform to rows
  const rows: MachineTypeRow[] = useMemo(
    () => (machineTypes ?? []).map((mt: any) => ({
      id: Number(mt.id) || 0,
      name: mt.name ?? '',
      description: mt.description ?? '',
    })),
    [machineTypes],
  );

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (data: { id?: number; name: string; description: string; toolIds?: string[]; safetyWarningIds?: string[] }) => {
      const payload: any = { name: data.name, description: data.description, toolIds: data.toolIds, safetyWarningIds: data.safetyWarningIds };
      if (data.id) {
        return machineTypeApi.updateMachineType({ id: String(data.id), machineType: payload } as any);
      }
      return machineTypeApi.createMachineType({ machineType: payload } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-types'] });
      setDrawerOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => machineTypeApi.deleteMachineType({ id: String(id) } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-types'] });
      setConfirmOpen(false);
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingId(null);
    setFormName('');
    setFormDescription('');
    setFormToolIds([]);
    setFormWarningIds([]);
    setErrors({});
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row: MachineTypeRow) => {
    setEditingId(Number(row.id));
    setFormName(row.name);
    setFormDescription(row.description ?? '');
    const fullMt: any = (machineTypes ?? []).find((mt: any) => (mt.id ?? '') === row.id);
    setFormToolIds(fullMt?.toolIds ?? []);
    setFormWarningIds(fullMt?.safetyWarningIds ?? []);
    setErrors({});
    setDrawerOpen(true);
  }, [machineTypes]);

  const handleSave = useCallback(() => {
    if (!formName.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }
    saveMutation.mutate({
      id: editingId ?? undefined,
      name: formName.trim(),
      description: formDescription.trim(),
      toolIds: formToolIds,
      safetyWarningIds: formWarningIds,
    });
  }, [formName, formDescription, formToolIds, formWarningIds, editingId, saveMutation]);

  // Row actions
  const rowActions: RowAction<MachineTypeRow>[] = [
    { label: 'Edit', icon: EditIcon, onClick: handleEdit },
    { label: 'Delete', icon: DeleteIcon, onClick: (row) => { setDeleteId(Number(row.id)); setConfirmOpen(true); } },
  ];

  // Drawer actions
  const drawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setDrawerOpen(false) },
    { label: editingId ? 'Update' : 'Create', variant: 'contained', onClick: handleSave, disabled: !formName },
  ];

  return (
    <>
      <FilterableDataTable<MachineTypeRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        totalCount={rows.length}
        page={0}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={rowActions}
        primaryAction={{ label: 'Add Machine Type', onClick: handleAdd }}
        searchPlaceholder="Search machine types..."
      />
      <DrawerPanel
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Edit Machine Type' : 'New Machine Type'}
        width="narrow"
        actions={drawerActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={formName}
            onChange={(e) => { setFormName(e.target.value); setErrors({}); }}
            required
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={3}
          />
          <Autocomplete
            multiple
            options={toolsQuery.data ?? []}
            getOptionLabel={(opt: any) => opt.name ?? ''}
            value={(toolsQuery.data ?? []).filter((t: any) => formToolIds.includes(t.id))}
            onChange={(_, val) => setFormToolIds(val.map((v: any) => v.id))}
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => (
                <Chip size="small" label={option.name} {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Tools Required" size="small" />}
            isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
          />
          <Autocomplete
            multiple
            options={warningsQuery.data ?? []}
            getOptionLabel={(opt: any) => opt.title ?? ''}
            value={(warningsQuery.data ?? []).filter((w: any) => formWarningIds.includes(w.id))}
            onChange={(_, val) => setFormWarningIds(val.map((v: any) => v.id))}
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => (
                <Chip size="small" label={option.title} color="warning" {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Safety Warnings" size="small" />}
            isOptionEqualToValue={(opt: any, val: any) => opt.id === val.id}
          />
        </Box>
      </DrawerPanel>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deleteId != null && deleteMutation.mutate(deleteId)}
        title="Delete Machine Type"
        message="Are you sure you want to delete this machine type? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default MachineTypesPanel;
